import { ButtonProps, chakra } from '@chakra-ui/react'
import { useDay } from '@datepicker-react/hooks'
import React, { useContext, useRef } from 'react'

import { DatePickerContext } from './DatePickerContext'

interface DPDayProps {
  date: Date
  dayLabel: string
}

enum DayState {
  default = 'default',
  disabled = 'disabled',
  hoveringSelectRange = 'hovering',
  initialSelect = 'initial',
  selectedRange = 'selected',
}

const sxDayButton = {
  '&.disabled': {
    color: 'neutral.50',
  },
  '&.hovering': {
    background: 'neutral.10',
  },
  '&.initial,&.initial.hovering,&.selected': {
    background: 'deepPurple',
    color: 'white.0',
  },
  // Last selected
  '&.selected ~ .initial.selected': {
    borderRadius: '0 3px 3px 0',
  },

  '&::before': {
    content: '""',
    paddingTop: '100%',
  },

  '&:hover:not(.selected):not(.initial)': {
    background: 'rgba(0, 0, 0, 0.05)',
  },
  // Part of range selected
  'div ~ &.selected:not(.initial)': {
    borderRadius: 0,
  },
}

const DayButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => (
    <chakra.button
      ref={ref}
      alignItems="center"
      borderRadius="3px"
      cursor="pointer"
      display="inline-flex"
      justifyContent="center"
      sx={sxDayButton}
      transition="background-color 0.2s ease-in"
      type="button"
      {...props}
    />
  ),
)
DayButton.displayName = 'DayButton'

export const DPDay = ({ date, dayLabel }: DPDayProps) => {
  const dayRef = useRef<HTMLButtonElement>(null)

  const {
    focusedDate,
    isDateBlocked,
    isDateFocused,
    isDateHovered,
    isDateSelected,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateHover,
    onDateSelect,
  } = useContext(DatePickerContext)

  const {
    disabledDate,
    isSelected,
    isSelectedStartOrEnd,
    isWithinHoverRange,
    onClick,
    onKeyDown,
    onMouseEnter,
    tabIndex,
  } = useDay({
    date,
    dayRef,
    focusedDate,
    isDateBlocked,
    isDateFocused,
    isDateHovered,
    isDateSelected,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateHover,
    onDateSelect,
  })

  if (!dayLabel) {
    return <div />
  }

  const selectState = (): string => {
    const classNames = []
    if (isSelected) classNames.push(DayState.selectedRange)
    if (isSelectedStartOrEnd) classNames.push(DayState.initialSelect)
    if (isWithinHoverRange) classNames.push(DayState.hoveringSelectRange)
    if (disabledDate) classNames.push(DayState.disabled)
    return classNames.join(' ')
  }

  return (
    <DayButton
      ref={dayRef}
      className={selectState()}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        if (onClick) {
          onClick()
        }
      }}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      tabIndex={tabIndex}
    >
      {dayLabel}
    </DayButton>
  )
}
